import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Check, Minus, Power, PowerOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import DataTable from '../DataTable';
import LeadSourceModal from './LeadSourceModal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';
import SmartSearchBar from '../leads/SmartSearchBar';
import { LeadSourceDto, CreateLeadSourceDto, UpdateLeadSourceDto, leadSourcesApi } from '../../services/settingsService';
import { handleApiError } from '../../utils/errorHandler';

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const LeadSourcesTab: React.FC = () => {
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLeadSource, setSelectedLeadSource] = useState<LeadSourceDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leadSourceToDelete, setLeadSourceToDelete] = useState<LeadSourceDto | null>(null);
  const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
  const [leadSourceToToggle, setLeadSourceToToggle] = useState<LeadSourceDto | null>(null);

  useEffect(() => {
    loadLeadSources();
  }, []);

  const loadLeadSources = async () => {
    try {
      setLoading(true);
      const data = await leadSourcesApi.getAll();
      setLeadSources(data);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת הרשומות'));
    } finally {
      setLoading(false);
    }
  };

  // Filter based on search (client-side over the small settings list)
  const filteredLeadSources = useMemo(() => {
    if (!searchTerm) return leadSources;
    const search = searchTerm.toLowerCase();
    return leadSources.filter(source =>
      source.name.toLowerCase().includes(search) ||
      source.description?.toLowerCase().includes(search) ||
      source.linkedUserEmail?.toLowerCase().includes(search)
    );
  }, [leadSources, searchTerm]);

  const handleAddLeadSource = () => {
    setSelectedLeadSource(null);
    setModalOpen(true);
  };

  const handleEditLeadSource = (leadSource: LeadSourceDto) => {
    setSelectedLeadSource(leadSource);
    setModalOpen(true);
  };

  const handleDeleteLeadSource = (leadSource: LeadSourceDto) => {
    setLeadSourceToDelete(leadSource);
    setDeleteConfirmOpen(true);
  };

  const handleToggleActive = (leadSource: LeadSourceDto) => {
    setLeadSourceToToggle(leadSource);
    setToggleConfirmOpen(true);
  };

  const handleSaveLeadSource = async (leadSourceData: CreateLeadSourceDto | UpdateLeadSourceDto) => {
    try {
      if (selectedLeadSource) {
        await leadSourcesApi.update(selectedLeadSource.id, leadSourceData as UpdateLeadSourceDto);
      } else {
        await leadSourcesApi.create(leadSourceData as CreateLeadSourceDto);
      }
      await loadLeadSources();
    } catch (error) {
      console.error('שגיאה בשמירת הרשומה:', error);
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!leadSourceToDelete) return;

    try {
      await leadSourcesApi.delete(leadSourceToDelete.id);
      await loadLeadSources();
      toast.success('הרשומה נמחקה בהצלחה');
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה במחיקת הרשומה'));
    } finally {
      setDeleteConfirmOpen(false);
      setLeadSourceToDelete(null);
    }
  };

  const confirmToggle = async () => {
    if (!leadSourceToToggle) return;
    const next = !leadSourceToToggle.isActive;

    try {
      // עדכון דרך אותו endpoint (אין endpoint ייעודי; תבנית Departments). שולחים את כל השדות כדי לשמרם.
      await leadSourcesApi.update(leadSourceToToggle.id, {
        name: leadSourceToToggle.name,
        description: leadSourceToToggle.description,
        isActive: next,
        type: leadSourceToToggle.type,
        defaultSigningCommissionAmount: leadSourceToToggle.defaultSigningCommissionAmount ?? null,
        defaultApprovalCommissionPercentage: leadSourceToToggle.defaultApprovalCommissionPercentage ?? null,
        createLogin: false,
      });
      await loadLeadSources();
      toast.success(next ? 'הרשומה הופעלה' : 'הרשומה הושבתה');
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון הסטטוס'));
    } finally {
      setToggleConfirmOpen(false);
      setLeadSourceToToggle(null);
    }
  };

  const buildToggleMessage = () => {
    if (!leadSourceToToggle) return '';
    const hasLogin = !!leadSourceToToggle.linkedUserId;
    if (leadSourceToToggle.isActive) {
      return `האם להשבית את "${leadSourceToToggle.name}"?` +
        (hasLogin ? ' חשבון ההתחברות המקושר יושבת גם הוא ולא יוכל להתחבר.' : '');
    }
    return `האם להפעיל מחדש את "${leadSourceToToggle.name}"?` +
      (hasLogin ? ' חשבון ההתחברות המקושר יופעל גם הוא.' : '');
  };

  const columns = [
    {
      key: 'name',
      title: 'שם',
      width: '190px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'typeName',
      title: 'סוג',
      width: '90px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'description',
      title: 'תיאור',
      width: '200px',
      render: (value: string) => (
        <CenteredCell>
          <SecondaryText>{value || '-'}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'linkedUserEmail',
      title: 'התחברות',
      width: '180px',
      render: (_: any, leadSource: LeadSourceDto) => (
        <CenteredCell>
          {leadSource.linkedUserId ? (
            <SecondaryText>
              {leadSource.linkedUserEmail}
              {leadSource.linkedUserActive === false ? ' (מושבת)' : ''}
            </SecondaryText>
          ) : (
            <SecondaryText>-</SecondaryText>
          )}
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '80px',
      render: (value: boolean) => (
        <CenteredCell>
          {value ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '130px',
      render: (_: any, leadSource: LeadSourceDto) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => handleEditLeadSource(leadSource)}
            title="עריכה"
          />
          <IconButton
            icon={leadSource.isActive ? PowerOff : Power}
            onClick={() => handleToggleActive(leadSource)}
            title={leadSource.isActive ? 'השבתה' : 'הפעלה'}
            variant={leadSource.isActive ? 'danger' : undefined}
          />
          <IconButton
            icon={Trash2}
            onClick={() => handleDeleteLeadSource(leadSource)}
            title="מחיקה"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <>
      <TabHeader>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש סוכנים / ארגונים..."
          />
        </SearchWrapper>
        <Button variant="primary" onClick={handleAddLeadSource}>
          <Plus size={16} />
          סוכן / ארגון חדש
        </Button>
      </TabHeader>

      <DataTable
        data={filteredLeadSources}
        columns={columns}
        loading={loading}
      />

      <LeadSourceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveLeadSource}
        leadSource={selectedLeadSource}
        title={selectedLeadSource ? 'עריכת סוכן / ארגון' : 'הוספת סוכן / ארגון'}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="מחיקת רשומה"
        message={`האם אתה בטוח שברצונך למחוק את "${leadSourceToDelete?.name}"?`}
        type="danger"
      />

      <ConfirmDialog
        isOpen={toggleConfirmOpen}
        onClose={() => setToggleConfirmOpen(false)}
        onConfirm={confirmToggle}
        title={leadSourceToToggle?.isActive ? 'השבתת רשומה' : 'הפעלת רשומה'}
        message={buildToggleMessage()}
        type={leadSourceToToggle?.isActive ? 'danger' : 'info'}
      />
    </>
  );
};

export default LeadSourcesTab;
